import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { ButtonComponent } from '@examdojo/core/button';
import { DateTimeFormat, DateTimeService } from '@examdojo/core/date-time';
import { IconComponent } from '@examdojo/core/icon';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { PdfViewerComponent } from '@examdojo/core/pdf-viewer';
import { ToastService } from '@examdojo/core/toast';
import { IonContent, IonFooter, IonHeader, IonTitle, IonToolbar } from '@ionic/angular/standalone';
import { ReceiptViewerDialogService } from './receipt-viewer-dialog.service';

@Component({
  selector: 'dojo-receipt-viewer-dialog',
  imports: [
    IonHeader,
    IonToolbar,
    IonTitle,
    IonFooter,
    IonContent,
    ButtonComponent,
    IconComponent,
    IconButtonComponent,
    PdfViewerComponent,
  ],
  template: `
    <ion-header>
      <ion-toolbar class="toolbar-with-closing-button !px-2 md:!px-4">
        <div slot="start" class="flex">
          <dojo-icon-button (click)="dismiss()" [icon]="'times'" />
        </div>
        <ion-title class="common-ion-title">
          <span>Invoice - {{ formattedPeriod() }}</span>
        </ion-title>
        <div slot="end" class="hidden md:flex md:w-8"></div>
      </ion-toolbar>
    </ion-header>

    <ion-content [scrollY]="false" class="flex flex-col">
      <div class="flex h-full flex-col">
        <dojo-pdf-viewer [src]="receiptUrl()" [name]="'Invoice_' + period() + '.pdf'" [zoom]="zoomLevel()" />
      </div>
    </ion-content>

    <ion-footer class="border-t border-neutral-200 shadow-none">
      <div class="flex flex-col gap-3 p-2 pb-6 md:flex-row md:justify-between md:p-4">
        <dojo-button [fill]="'outline'" (click)="dismiss()"> Close </dojo-button>
        <dojo-button [fill]="'solid'" (click)="downloadInvoice()">
          <dojo-icon [icon]="'download'" class="mr-2" />
          Download Invoice
        </dojo-button>
      </div>
    </ion-footer>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex h-full flex-col' },
})
export class ReceiptViewerDialogComponent {
  private readonly receiptViewerDialogService = inject(ReceiptViewerDialogService);
  private readonly toastService = inject(ToastService);
  private readonly dateTimeService = inject(DateTimeService);

  readonly receiptUrl = input.required<string>();
  readonly period = input.required<string>();

  readonly zoomLevel = computed(() => 100);

  readonly formattedPeriod = computed(() => {
    const period = this.period();
    const [year, month] = period.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return this.dateTimeService.formatDate(date, DateTimeFormat.LongMonthYear);
  });

  dismiss(): void {
    this.receiptViewerDialogService.dismiss();
  }

  async downloadInvoice(): Promise<void> {
    try {
      const response = await fetch(this.receiptUrl());
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${this.period()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      this.toastService.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      this.toastService.error('Failed to download invoice');
    }
  }
}
